import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { Doughnut, Bar, getElementAtEvent } from "react-chartjs-2";
import { Button, DatePicker, Modal, Select, Table, Tooltip } from "antd";
import ReportJewelryReportService from "../../../services/reports-Jewelry-service";
import { BlobToDownloadFile } from "../../../helper/commanFunction";
import DefaultImage from "../../../assets/images/icons/sampleimage.jpg";
import { bucketTopN } from "../../ChartsGroup/DiamondReportsCharts/_shared/bucketTopN";
import CustomLegend from "../../ChartsGroup/DiamondReportsCharts/_shared/CustomLegend";
import ChartState from "../../ChartsGroup/DiamondReportsCharts/_shared/ChartState";
import { donutOptions, barOptions } from "../../ChartsGroup/DiamondReportsCharts/_shared/chartTheme";
import { exportChartPdf } from "../../ChartsGroup/DiamondReportsCharts/_shared/exportChartPdf";
import { trendFor } from "../../ChartsGroup/DiamondReportsCharts/_shared/kpiHelpers";
import { extractDealerIds, mapSortColumn, fetchPriorPeriodTotalClicks } from "./_shared/jewelryHelpers";

const { RangePicker } = DatePicker;
const { Option } = Select;

// Flagship combined page (Story 1696, Dealer scope). Hand-composed rather
// than built on ReportScaffold -- Scaffold only supports one chart card, and
// this page shows up to 4 side by side (precedent: DiamondReportsChartsIndComp,
// the diamond suite's equivalent multi-chart page, which is also
// hand-composed rather than Scaffold-based). Unlike the Admin flagship, this
// Dealer page does not have the Vendor/Retailer JSX-swap bug -- each panel's
// heading already matches its own data -- so this is a pure chart/KPI/insight
// redesign, not a bug fix.
const bandStart = (label) => {
  if (!label || label === "Call For Price") return Infinity;
  const match = String(label).replace(/,/g, "").match(/[\d.]+/);
  return match ? Number(match[0]) : Infinity;
};

const parsePriceBand = (label) => {
  if (label === "Call For Price") return { price: "Call For Price", price1: "", price2: "" };
  const [low, high] = label.split("-");
  const lowPrice1 = parseInt(low.replace(/^.*?\$/, "").replace(/,/g, "").trim(), 10);
  const highPrice2 = parseInt((high || "").replace(/[$,]/g, ""), 10);
  // Backend's Price1/Price2 fields are typed string -- a bare number here
  // fails model binding (400) and the table never updates.
  return {
    price: "",
    price1: low.includes("Under") ? "0" : String(lowPrice1),
    price2: low.includes("Under") ? String(lowPrice1) : (highPrice2 ? String(highPrice2) : ""),
  };
};

const emptyFilter = { category: "", vendor: "", retailer: "", price: "", price1: "", price2: "" };

const JeweleryChartReports = () => {
  const { t } = useTranslation();
  const loginDetials = useSelector((state) => state.loginReducer);

  const [dealerId, setDealerId] = useState();
  const [forDealer, setForDealer] = useState("");
  const [dealerMembershipType, setDealerMembershipType] = useState("");
  const [dataRangeFrom, setDataRangeFrom] = useState(moment().subtract(30, "days").format("YYYY-MM-DD"));
  const [dataRangeTo, setDataRangeTo] = useState(moment().format("YYYY-MM-DD"));
  const [vendorIds, setVendorIds] = useState([]);
  const [retailerIds, setRetailerIds] = useState([]);
  const [vendorList, setVendorList] = useState([]);
  const [retailerList, setRetailerList] = useState([]);

  const [categoryData, setCategoryData] = useState([]);
  const [priceData, setPriceData] = useState([]);
  const [vendorClicksData, setVendorClicksData] = useState([]);
  const [retailerClicksData, setRetailerClicksData] = useState([]);
  const [chartsLoading, setChartsLoading] = useState(false);
  const [totalClicks, setTotalClicks] = useState(0);
  const [priorTotalClicks, setPriorTotalClicks] = useState(null);

  const [filter, setFilter] = useState(emptyFilter);
  const [refetchToken, setRefetchToken] = useState(0);
  const [pdfLoading, setPdfLoading] = useState(false);

  const [dataSource, setDataSource] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [tableLoading, setTableLoading] = useState(false);
  const [csvLoading, setCsvLoading] = useState(false);
  const [tablePdfLoading, setTablePdfLoading] = useState(false);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [sortColumnName, setSortColumnName] = useState("Date");
  const [sortType, setSortType] = useState("desc");
  const [searchQuery, setSearchQuery] = useState("");
  const isFirstSearchRender = useRef(true);
  const [visible, setVisible] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");

  const categoryChartRef = useRef();
  const priceChartRef = useRef();
  const vendorChartRef = useRef();
  const retailerChartRef = useRef();

  // Ported exactly from the pre-redesign page's 3-way branch: Admin sees
  // both; a Retailer-type dealer sees the Vendor breakdown (who's driving
  // their traffic); everyone else (Vendor/VendorDiamond/Combined/unset) sees
  // the Retailer breakdown.
  const showVendorChart = dealerMembershipType === "Admin" || dealerMembershipType === "Retailer";
  // dealerMembershipType is "" until GetForDealer resolves; !== "Retailer" was
  // true for that empty string too, so this briefly flashed true (and the
  // panel with it) on every load before settling to its real value.
  const showRetailerChart = dealerMembershipType !== "" && dealerMembershipType !== "Retailer";

  useEffect(() => {
    if (loginDetials?.loginDetials?.responseData?.logInSucceeded) {
      const id = loginDetials.loginDetials.responseData.dealerId;
      setDealerId(id);
      handleCheckDealermembers(id);
    }
    handleGetVendorDropdownData();
    handleGetRetailerDropdownData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCheckDealermembers = (id) => {
    ReportJewelryReportService.GetForDealer({ dealerID: id }).then((response) => {
      if (response.data.message === "Success") {
        const memberType = loginDetials.loginDetials.responseData.dealerMemberTypeName;
        setDealerMembershipType(memberType);
        const fd = memberType === "Vendor" || memberType === "VendorDiamond" ? 1 : memberType === "Retailer" || memberType === "Combined" ? 2 : 3;
        setForDealer(fd);
        handleLoadAllCharts(id, fd, dataRangeFrom, dataRangeTo, [], []);
        handleFetchTable(id, fd, 1, pageSize, sortColumnName, sortType, emptyFilter, "");
      }
    });
  };

  const handleGetVendorDropdownData = () => {
    const id = loginDetials?.loginDetials?.responseData?.dealerId;
    ReportJewelryReportService.GetVendorDropdownList({ dealerId: id, reporttype: "Dealer" }).then((response) => {
      if (response.data.message === "Success") setVendorList(response.data.responseData);
    });
  };

  const handleGetRetailerDropdownData = () => {
    const id = loginDetials?.loginDetials?.responseData?.dealerId;
    ReportJewelryReportService.GetRetailerDropdownList({ dealerId: id, reporttype: "Dealer" }).then((response) => {
      if (response.data.message === "Success") setRetailerList(response.data.responseData);
    });
  };

  const handleVendorChange = (values) => {
    setVendorIds(values);
    if (values.length === 0) {
      handleGetRetailerDropdownData();
    } else {
      ReportJewelryReportService.ChangeRetailerList({ vendorIds: extractDealerIds(values).toString() }).then((response) => {
        if (response.data.message === "Success") setRetailerList(response.data.responseData);
      });
    }
  };

  const handleRetailerChange = (values) => {
    setRetailerIds(values);
    if (values.length === 0) {
      handleGetVendorDropdownData();
    } else {
      ReportJewelryReportService.ChangeVendorList({ retailerIds: extractDealerIds(values).toString() }).then((response) => {
        if (response.data.message === "Success") setVendorList(response.data.responseData);
      });
    }
  };

  const handleLoadAllCharts = (dealerIdArg, forDealerArg, fromDate, toDate, vIds, rIds) => {
    setChartsLoading(true);
    const base = {
      dealerID: dealerIdArg.toString(),
      forDealer: forDealerArg ? forDealerArg.toString() : "",
      fromDate: moment(fromDate).format("YYYY-MM-DD"),
      toDate: moment(toDate).format("YYYY-MM-DD"),
      retailerIds: extractDealerIds(rIds).toString(),
      vendorIds: extractDealerIds(vIds).toString(),
      reporttype: "Dealer",
    };
    // allSettled, not all -- one endpoint failing/throwing must not blank
    // out the other three panels (that was the bug: a single rejection was
    // wiping all 4 charts to empty).
    Promise.allSettled([
      ReportJewelryReportService.GetClicksBasedOnCategory(base),
      ReportJewelryReportService.GetClicksBasedOnPrice(base),
      ReportJewelryReportService.GetClicksBasedOnTopVendor(base),
      // Ported as-is from the pre-redesign page: the retailer-chart call
      // never sends retailerIds (matches the standalone Retailer Clicks
      // report too), and dealerID 1785 forces forDealer='3' -- looks like a
      // one-off data-correction for that account, not touching it.
      ReportJewelryReportService.GetClicksBasedOnRetailer({ ...base, retailerIds: "", forDealer: dealerIdArg === 1785 ? "3" : base.forDealer }),
    ]).then(([categoryRes, priceRes, vendorRes, retailerRes]) => {
      const okData = (settled, path) => {
        if (settled.status !== "fulfilled") {
          console.error("Jewelry chart fetch failed:", settled.reason);
          return [];
        }
        const res = settled.value;
        return res.data.message === "Success" ? path(res.data.responseData) || [] : [];
      };
      const category = okData(categoryRes, (d) => d?.Table);
      const price = okData(priceRes, (d) => d?.Table);
      const vendor = okData(vendorRes, (d) => d?.Table);
      const retailer = okData(retailerRes, (d) => d);
      setCategoryData(category);
      setPriceData(price);
      setVendorClicksData(vendor);
      setRetailerClicksData(retailer);
      setTotalClicks(category.reduce((sum, row) => sum + Number(row.count || 0), 0));
      setChartsLoading(false);
    });

    setPriorTotalClicks(null);
    fetchPriorPeriodTotalClicks(
      ReportJewelryReportService.GetClicksBasedOnCategory,
      (pFrom, pTo) => ({ ...base, fromDate: pFrom, toDate: pTo }),
      fromDate,
      toDate,
      "count"
    ).then(setPriorTotalClicks);
  };

  // dealerIdArg/forDealerArg are passed in explicitly (not read from dealerId/forDealer
  // state) so the very first fetch -- fired from within the GetForDealer .then() before
  // setDealerId/setForDealer have flushed to a re-render -- still sends real values
  // instead of stale/empty closures (was causing a 400 on first load).
  const buildTableInput = (dealerIdArg, forDealerArg, pageNumber, size, sortCol, sortOrder, filterArg, searchText) => ({
    dealerID: dealerIdArg != null ? dealerIdArg.toString() : "",
    forDealer: forDealerArg ? forDealerArg.toString() : "",
    fromDate: dataRangeFrom ? moment(dataRangeFrom).format("YYYY-MM-DD") : "2020-08-17",
    toDate: dataRangeTo ? moment(dataRangeTo).format("YYYY-MM-DD") : "2022-09-15",
    retailerIds: extractDealerIds(retailerIds).toString(),
    vendorIds: extractDealerIds(vendorIds).toString(),
    category: filterArg.category || "",
    metalType: "",
    metalColorType: "",
    price: filterArg.price || "",
    price1: filterArg.price1 != null ? filterArg.price1.toString() : "",
    price2: filterArg.price2 != null ? filterArg.price2.toString() : "",
    vendor: filterArg.vendor || "",
    retailer: filterArg.retailer || "",
    pageIndex: pageNumber || 1,
    pageSize: size || 15,
    sortColumnName: mapSortColumn(sortCol),
    sortOrder: sortOrder || "desc",
    reporttype: "Dealer",
    SearchText: searchText || "",
  });

  const handleFetchTable = (dealerIdArg, forDealerArg, pageNumber, size, sortCol, sortOrder, filterArg, searchText) => {
    if (dealerIdArg == null) return;
    setTableLoading(true);
    ReportJewelryReportService.GetJewelryTableChartData(buildTableInput(dealerIdArg, forDealerArg, pageNumber, size, sortCol, sortOrder, filterArg, searchText))
      .then((response) => {
        setTableLoading(false);
        if (response.data.message === "Success" && response.data.responseData) {
          setTotalCount(response.data.responseData?.[0]?.totalRecords || 0);
          setDataSource(response.data.responseData || []);
        } else {
          setTotalCount(0);
          setDataSource([]);
        }
      })
      .catch(() => {
        setTableLoading(false);
        setTotalCount(0);
        setDataSource([]);
      });
  };

  useEffect(() => {
    if (dealerId == null) return;
    setPageNo(1);
    handleFetchTable(dealerId, forDealer, 1, pageSize, sortColumnName, sortType, filter, searchQuery.trim());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, refetchToken]);

  useEffect(() => {
    if (isFirstSearchRender.current) {
      isFirstSearchRender.current = false;
      return;
    }
    const handle = setTimeout(() => {
      setPageNo(1);
      handleFetchTable(dealerId, forDealer, 1, pageSize, sortColumnName, sortType, filter, searchQuery.trim());
    }, 400);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const onDateChange = (dates, dateStrings) => {
    if (dates) {
      setDataRangeFrom(dateStrings[0]);
      setDataRangeTo(dateStrings[1]);
    }
  };

  const handleApplyFilter = () => {
    setFilter(emptyFilter);
    setSearchQuery("");
    handleLoadAllCharts(dealerId, forDealer, dataRangeFrom, dataRangeTo, vendorIds, retailerIds);
    setRefetchToken((tok) => tok + 1);
  };

  const handleCategoryClick = (category) => setFilter({ ...emptyFilter, category });
  const handleVendorClick = (vendor) => setFilter({ ...emptyFilter, vendor });
  const handleRetailerClick = (retailer) => setFilter({ ...emptyFilter, retailer });
  const handlePriceClick = (label) => setFilter({ ...emptyFilter, ...parsePriceBand(label) });

  const handleDownloadChart = async () => {
    setPdfLoading(true);
    const vendorNames = vendorIds.length === 0 ? t("All Vendors") : vendorList.filter((v) => extractDealerIds(vendorIds).includes(v.dealerID.toString())).map((v) => v.dealerCompany).join(", ");
    const retailerNames = retailerIds.length === 0 ? t("All Retailers") : retailerList.filter((v) => extractDealerIds(retailerIds).includes(v.dealerID.toString())).map((v) => v.dealerCompany).join(", ");
    try {
      await exportChartPdf({
        elementId: "pdf-content",
        title: t("Jewelry Chart Report"),
        dealerCompany: loginDetials?.loginDetials?.responseData?.dealerCompany,
        dealerId,
        dataRangeFrom,
        dataRangeTo,
        infoLines: [
          ...(dealerMembershipType !== "" && !isVendor ? [`Vendors: ${vendorNames}`] : []),
          ...(dealerMembershipType !== "" && !isRetailer ? [`Retailers: ${retailerNames}`] : []),
          `Total Clicks: ${totalClicks}`,
        ],
        fileName: "Jewelry-Chart-Report.pdf",
      });
    } finally {
      setPdfLoading(false);
    }
  };

  const handleTableChange = (pagination, _filters, sorter) => {
    const tableBody = document.querySelector(".edr-jewelry-flagship-table .ant-table-body");
    if (tableBody) tableBody.scrollTop = 0;
    let type = "desc";
    let column = "date";
    if (sorter.order === "descend") {
      type = "DESC";
      column = sorter.field;
    } else if (sorter.order === "ascend") {
      type = "ASC";
      column = sorter.field;
    }
    setSortType(type);
    setSortColumnName(column);
    setPageNo(pagination.current);
    setPageSize(pagination.pageSize);
    handleFetchTable(dealerId, forDealer, pagination.current, pagination.pageSize, column, type, filter, searchQuery.trim());
  };

  // Uses the same Admin_JewelryReportForTableChart endpoint (+ buildTableInput
  // payload shape) as the on-screen "Product Click Log" table -- the old
  // ExportChartReportToCSV/PDF calls hit a separate, stale, wrongly-scoped
  // SP (JewelryVendorTableChartAll) that returned unrelated/duplicated rows.
  const handleExportCSV = () => {
    setCsvLoading(true);
    ReportJewelryReportService.ExportDataToCSV(
      buildTableInput(dealerId, forDealer, pageNo, pageSize, sortColumnName, "asc", filter, searchQuery.trim())
    )
      .then((response) => {
        setCsvLoading(false);
        const fileName = `${(loginDetials?.loginDetials?.responseData?.dealerCompany || "").replace(/\s+/g, "")}_JewelryChartReports_${new Date()
          .toJSON()
          .slice(0, 10)
          .split("-")
          .slice(1)
          .concat(new Date().toJSON().slice(0, 4))
          .join("_")}.csv`;
        BlobToDownloadFile(response.data, fileName);
      })
      .catch(() => setCsvLoading(false));
  };

  const handleExportTablePDF = () => {
    setTablePdfLoading(true);
    ReportJewelryReportService.ExportDataToPDF(
      buildTableInput(dealerId, forDealer, pageNo, pageSize, sortColumnName, "asc", filter, searchQuery.trim())
    )
      .then((response) => {
        setTablePdfLoading(false);
        const fileName = `${(loginDetials?.loginDetials?.responseData?.dealerCompany || "").replace(/\s+/g, "")}_JewelryChartReports_${new Date()
          .toJSON()
          .slice(0, 10)
          .split("-")
          .slice(1)
          .concat(new Date().toJSON().slice(0, 4))
          .join("_")}.pdf`;
        BlobToDownloadFile(response.data, fileName, "pdf");
      })
      .catch(() => setTablePdfLoading(false));
  };

  const imageClickHandler = (img) => {
    setSelectedImage(img || DefaultImage);
    setVisible(true);
  };

  // Category / Vendor / Retailer are leaderboard-style (top-N + Others,
  // ranked by count). Price is kept in natural band order, matching the
  // approved prototype and the standalone Price report.
  const categoryChart = useMemo(
    () => bucketTopN(categoryData, { labelKey: "Category", valueKey: "count", chartLabel: "Category", othersLabel: t("Others") }),
    [categoryData, t]
  );
  const vendorChart = useMemo(
    () => bucketTopN(vendorClicksData, { labelKey: "DealerCompany", valueKey: "Total", chartLabel: "Vendor", othersLabel: t("Others") }),
    [vendorClicksData, t]
  );
  const retailerChart = useMemo(
    () => bucketTopN(retailerClicksData, { labelKey: "dealerCompany", valueKey: "total", chartLabel: "Retailer", othersLabel: t("Others") }),
    [retailerClicksData, t]
  );
  const priceRows = useMemo(() => {
    const list = priceData.filter((item) => Number(item.total) > 0);
    const sorted = [...list].sort((a, b) => bandStart(a.Range) - bandStart(b.Range));
    const total = sorted.reduce((sum, item) => sum + Number(item.total), 0);
    return sorted.map((item) => ({
      label: item.Range,
      value: Number(item.total),
      percentage: total > 0 ? ((Number(item.total) / total) * 100).toFixed(1) : 0,
    }));
  }, [priceData]);
  const priceChartData = [["Price Band", "Count"], ...priceRows.map((r) => [`${r.label} (${r.percentage}%)`, r.value])];

  const makeClickHandler = (ref, rows, onPick) => (event) => {
    if (!ref.current) return;
    const [el] = getElementAtEvent(ref.current, event);
    if (!el) return;
    const label = rows[el.index]?.[0]?.replace(/\s*\([^)]*\)\s*$/, "");
    if (label && label !== t("Others")) onPick(label);
  };

  const topCategory = [...categoryData].sort((a, b) => Number(b.count || 0) - Number(a.count || 0))[0];
  const topPriceBand = [...priceRows].sort((a, b) => b.value - a.value)[0];
  const topVendor = [...vendorClicksData].sort((a, b) => Number(b.Total || 0) - Number(a.Total || 0))[0];
  const topRetailer = [...retailerClicksData].sort((a, b) => Number(b.total || 0) - Number(a.total || 0))[0];
  const clicksTrend = priorTotalClicks != null ? trendFor([{ Metric: "TotalClicks", CurrentValue: totalClicks, PriorValue: priorTotalClicks }], "TotalClicks") : null;

  const isVendor = dealerMembershipType === "Vendor" || dealerMembershipType === "VendorDiamond";
  const isRetailer = dealerMembershipType === "Retailer";

  return (
    <div className="pendjewelreq__section form__fields">
      <div className="row magento__mapping">
        <div className="col-md-12">
          <div className="edr-pagehead-row">
            <div className="edr-pagehead">
              <h1 className="edr-pagehead-title">{t("Jewelry Chart Report")}</h1>
              <div className="edr-pagehead-sub">
                {moment(dataRangeFrom).format("MMM D, YYYY")} – {moment(dataRangeTo).format("MMM D, YYYY")}
              </div>
            </div>
            <div className="edr-pagehead-actions">
              <Button className="ghost-btn" onClick={handleDownloadChart} loading={pdfLoading}>
                <i className="fa fa-download"></i> {t("Download Chart")}
              </Button>
              <button className="primary-btn" onClick={handleApplyFilter}>{t("Apply Filter")}</button>
            </div>
          </div>
        </div>

        <div className="col-md-12">
          <div className="row edr-filters">
            <div className="col-md-3">
              <div className="input__block dataRange-input-block datepicker__block">
                <label>{t("Date Range")}</label>
                <RangePicker
                  className="date-range-input"
                  ranges={{
                    Today: [moment(), moment()],
                    Yesterday: [moment().subtract(1, "days"), moment().subtract(1, "days")],
                    "Last 7 Days": [moment().subtract(7, "days"), moment()],
                    "Last 30 Days": [moment().subtract(30, "days"), moment()],
                    "This Month": [moment().startOf("month"), moment().endOf("month")],
                    "Last Month": [moment().subtract(1, "month").startOf("month"), moment().subtract(1, "months").endOf("month")],
                    "Last 90 Days": [moment().subtract(90, "days"), moment()],
                    "Year To Date": [moment().startOf("year"), moment()],
                  }}
                  format={"MM-DD-YYYY"}
                  onChange={onDateChange}
                  defaultValue={[moment().subtract(30, "days"), moment()]}
                  defaultPickerValue={[moment().subtract(30, "days"), moment()]}
                />
              </div>
            </div>
            {/* Bug 1849: Vendor(s)/Retailer(s) filters removed from regular Jewelry Reports
            {dealerMembershipType !== "" && !isVendor && (
              <div className="col-md-3">
                <div className="input__block">
                  <label>{t("Vendor(s)")}</label>
                  <Select className="border__grey" showSearch showArrow mode="multiple" placeholder={t("Select One or More Vendors")} optionFilterProp="children" value={vendorIds} onChange={handleVendorChange}>
                    {vendorList.map((item) => (
                      <Option key={`vendor-${item.dealerID}`} value={`${item.dealerID}|${item.dealerCompany}`}>{item.dealerCompany}</Option>
                    ))}
                  </Select>
                </div>
              </div>
            )}
            {dealerMembershipType !== "" && !isRetailer && (
              <div className="col-md-3">
                <div className="input__block">
                  <label>{t("Retailer(s)")}</label>
                  <Select className="border__grey" showSearch showArrow mode="multiple" placeholder={t("Select one or more retailer")} optionFilterProp="children" value={retailerIds} onChange={handleRetailerChange}>
                    {retailerList.map((item) => (
                      <Option key={`retailer-${item.dealerID}`} value={`${item.dealerID}|${item.dealerCompany}`}>{item.dealerCompany}</Option>
                    ))}
                  </Select>
                </div>
              </div>
            )}
            */}
          </div>
        </div>

        <div className="col-md-12">
          <div className="edr-kpis">
            <div className="edr-kpi">
              <div className="edr-kpi-lab">{t("Total Clicks")}</div>
              <div className="edr-kpi-val">{chartsLoading ? "—" : totalClicks.toLocaleString()}</div>
              {clicksTrend && (
                <div className={`edr-kpi-trend ${clicksTrend.up ? "edr-up" : "edr-down"}`}>
                  {clicksTrend.up ? "▲" : "▼"} {Math.abs(clicksTrend.change).toFixed(1)}% {t("vs prior period")}
                </div>
              )}
            </div>
            <div className="edr-kpi">
              <div className="edr-kpi-lab">{t("Top Category")}</div>
              <div className="edr-kpi-val">{topCategory?.Category ?? "—"}</div>
              {topCategory && <div className="edr-kpi-sub">{Number(topCategory.count).toLocaleString()} {t("clicks")}</div>}
            </div>
            <div className="edr-kpi">
              <div className="edr-kpi-lab">{t("Price Sweet Spot")}</div>
              <div className="edr-kpi-val">{topPriceBand?.label ?? "—"}</div>
              {topPriceBand && <div className="edr-kpi-sub">{topPriceBand.value.toLocaleString()} {t("clicks")}</div>}
            </div>
            {showVendorChart && (
              <div className="edr-kpi">
                <div className="edr-kpi-lab">{t("Top Vendor")}</div>
                <div className="edr-kpi-val">{topVendor?.DealerCompany ?? "—"}</div>
                {topVendor && <div className="edr-kpi-sub">{Number(topVendor.Total).toLocaleString()} {t("clicks")}</div>}
              </div>
            )}
            {showRetailerChart && (
              <div className="edr-kpi">
                <div className="edr-kpi-lab">{t("Top Retailer")}</div>
                <div className="edr-kpi-val">{topRetailer?.dealerCompany ?? "—"}</div>
                {topRetailer && <div className="edr-kpi-sub">{Number(topRetailer.total).toLocaleString()} {t("clicks")}</div>}
              </div>
            )}
          </div>
        </div>

        {topCategory && (
          <div className="col-md-12">
            <div className="edr-insight">
              💡 {t("Demand profile this period")}: {t("shoppers gravitate to")} <b>{topCategory.Category}</b>
              {topPriceBand && <> {t("in the")} <b>{topPriceBand.label}</b> {t("band")}</>}
              {topVendor && showVendorChart && <>, {t("with")} <b>{topVendor.DealerCompany}</b> {t("the standout vendor")}</>}
              {topRetailer && showRetailerChart && !showVendorChart && <>, {t("with")} <b>{topRetailer.dealerCompany}</b> {t("the standout retailer")}</>}.
            </div>
          </div>
        )}

        <div className="col-md-12" id="pdf-content">
          <div className="row">
            <div className="col-lg-6">
              <div className="edr-panel mt-4">
                <div className="edr-panel-head">
                  <label><b>{t("Jewelry Clicks Based on Category")}</b></label>
                </div>
                <div className="chart__section" style={{ display: "flex", justifyContent: "center", flexDirection: "column" }}>
                  {!chartsLoading && categoryChart.chartData.length > 1 ? (
                    <>
                      <CustomLegend chartData={categoryChart.chartData} colorData={categoryChart.colors} />
                      <div className="edr-chartwrap edr-tall">
                        <Doughnut
                          ref={categoryChartRef}
                          data={{
                            labels: categoryChart.chartData.slice(1).map((r) => r[0]),
                            datasets: [{ data: categoryChart.chartData.slice(1).map((r) => r[1]), backgroundColor: categoryChart.colors, borderColor: "#fff", borderWidth: 2 }],
                          }}
                          options={donutOptions}
                          onClick={makeClickHandler(categoryChartRef, categoryChart.chartData.slice(1), handleCategoryClick)}
                        />
                      </div>
                    </>
                  ) : (
                    <ChartState loading={chartsLoading} />
                  )}
                </div>
              </div>
            </div>

            <div className="col-lg-6">
              <div className="edr-panel mt-4">
                <div className="edr-panel-head">
                  <label><b>{t("Jewelry Clicks Based on Price")}</b></label>
                </div>
                <div className="chart__section" style={{ display: "flex", justifyContent: "center", flexDirection: "column" }}>
                  {!chartsLoading && priceRows.length > 0 ? (
                    <div className="edr-chartwrap edr-tall">
                      <Bar
                        ref={priceChartRef}
                        data={{
                          labels: priceChartData.slice(1).map((r) => r[0]),
                          datasets: [{ data: priceChartData.slice(1).map((r) => r[1]), backgroundColor: "#3b5bdb", borderRadius: 4 }],
                        }}
                        options={barOptions(false)}
                        onClick={makeClickHandler(priceChartRef, priceChartData.slice(1), handlePriceClick)}
                      />
                    </div>
                  ) : (
                    <ChartState loading={chartsLoading} />
                  )}
                </div>
              </div>
            </div>

            {showVendorChart && (
              <div className="col-lg-6">
                <div className="edr-panel mt-4">
                  <div className="edr-panel-head">
                    <label><b>{t("Jewelry Clicks Based on Vendor")}</b></label>
                  </div>
                  <div className="chart__section" style={{ display: "flex", justifyContent: "center", flexDirection: "column" }}>
                    {!chartsLoading && vendorChart.chartData.length > 1 ? (
                      <div className="edr-chartwrap edr-tall">
                        <Bar
                          ref={vendorChartRef}
                          data={{
                            labels: vendorChart.chartData.slice(1).map((r) => r[0]),
                            datasets: [{ data: vendorChart.chartData.slice(1).map((r) => r[1]), backgroundColor: vendorChart.colors, borderRadius: 4 }],
                          }}
                          options={barOptions(true)}
                          onClick={makeClickHandler(vendorChartRef, vendorChart.chartData.slice(1), handleVendorClick)}
                        />
                      </div>
                    ) : (
                      <ChartState loading={chartsLoading} />
                    )}
                  </div>
                </div>
              </div>
            )}

            {showRetailerChart && (
              <div className="col-lg-6">
                <div className="edr-panel mt-4">
                  <div className="edr-panel-head">
                    <label><b>{t("Jewelry Clicks Based on Retailer")}</b></label>
                  </div>
                  <div className="chart__section" style={{ display: "flex", justifyContent: "center", flexDirection: "column" }}>
                    {!chartsLoading && retailerChart.chartData.length > 1 ? (
                      <div className="edr-chartwrap edr-tall">
                        <Bar
                          ref={retailerChartRef}
                          data={{
                            labels: retailerChart.chartData.slice(1).map((r) => r[0]),
                            datasets: [{ data: retailerChart.chartData.slice(1).map((r) => r[1]), backgroundColor: retailerChart.colors, borderRadius: 4 }],
                          }}
                          options={barOptions(true)}
                          onClick={makeClickHandler(retailerChartRef, retailerChart.chartData.slice(1), handleRetailerClick)}
                        />
                      </div>
                    ) : (
                      <ChartState loading={chartsLoading} />
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="col-md-12">
          <div className="edr-panel mt-4">
            <div className="edr-panel-head">
              <div>
                <h3>{t("Product Click Log")}</h3>
                <div className="edr-panel-desc">{t("Individual product views · click a column to sort")}</div>
              </div>
              <div className="edr-pagehead-actions">
                <Button className="ghost-btn" onClick={handleExportCSV} loading={csvLoading}>
                  <i className="fa fa-download"></i> {t("CSV")}
                </Button>
                <Button className="ghost-btn" onClick={handleExportTablePDF} loading={tablePdfLoading}>
                  <i className="fa fa-download"></i> {t("PDF")}
                </Button>
              </div>
            </div>

            <div className="edr-tblbar">
              <input
                className="edr-tblsearch"
                placeholder={t("Search vendor, retailer, category, style…")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <span className="edr-badge">{t("{{count}} rows", { count: totalCount })}</span>
            </div>

            <div className="edr-detail-table edr-jewelry-flagship-table">
              <Table
                className="pendjewelreq__tbl"
                scroll={{ x: 1200, y: "calc(100vh - 340px)" }}
                loading={tableLoading}
                columns={[
                  {
                    title: <Tooltip title={t("Product Image")}>{t("Product Image")}</Tooltip>,
                    dataIndex: "imagePathNew",
                    render: (img) => (
                      <span onClick={() => imageClickHandler(img)} style={{ cursor: "pointer" }}>
                        <img src={img || DefaultImage} alt="" className="product__img" />
                      </span>
                    ),
                  },
                  { title: <Tooltip title={t("User IP Address")}>{t("User IP Address")}</Tooltip>, ellipsis: true, dataIndex: "userIPAddress", sorter: () => {} },
                  ...(dealerMembershipType !== "" && !isVendor
                    ? [{ title: <Tooltip title={t("Vendor")}>{t("Vendor")}</Tooltip>, ellipsis: true, dataIndex: "vendorName", sorter: () => {} }]
                    : []),
                  ...(dealerMembershipType !== "" && !isRetailer
                    ? [{ title: <Tooltip title={t("Retailer")}>{t("Retailer")}</Tooltip>, ellipsis: true, dataIndex: "retailerName", sorter: () => {} }]
                    : []),
                  { title: <Tooltip title={t("Collection")}>{t("Collection")}</Tooltip>, ellipsis: true, dataIndex: "collectionNew", sorter: () => {} },
                  { title: <Tooltip title={t("Category")}>{t("Category")}</Tooltip>, ellipsis: true, dataIndex: "category", sorter: () => {} },
                  {
                    title: <Tooltip title={t("Date")}>{t("Date")}</Tooltip>,
                    ellipsis: true,
                    dataIndex: "date",
                    defaultSortOrder: "descend",
                    sorter: () => {},
                    render: (date) => date && moment(date).format("MM-DD-YYYY, hh:mm:ss A"),
                  },
                  { title: <Tooltip title={t("Style Number")}>{t("Style Number")}</Tooltip>, ellipsis: true, dataIndex: "styleNumber", sorter: () => {} },
                  { title: <Tooltip title={t("Metal Type")}>{t("Metal Type")}</Tooltip>, ellipsis: true, dataIndex: "metalType", sorter: () => {} },
                  { title: <Tooltip title={t("Metal Color")}>{t("Metal Color")}</Tooltip>, ellipsis: true, dataIndex: "metalColor", sorter: () => {} },
                  {
                    title: <Tooltip title={t("Price")}>{t("Price")}</Tooltip>,
                    ellipsis: true,
                    dataIndex: "price",
                    sorter: () => {},
                    render: (price) => <Tooltip title={price}><span>{price}</span></Tooltip>,
                  },
                ]}
                dataSource={dataSource}
                pagination={{
                  size: "small",
                  total: totalCount,
                  pageSize: pageSize,
                  pageSizeOptions: [10, 15, 20, 50, 100],
                  responsive: true,
                  showSizeChanger: true,
                  current: pageNo,
                }}
                onChange={handleTableChange}
              />
            </div>
          </div>
        </div>
      </div>

      <Modal title="" centered visible={visible} onOk={() => setVisible(false)} onCancel={() => setVisible(false)} width={500} footer={false} className="image-modal">
        <img src={selectedImage} alt="" className="img-fluid" />
      </Modal>
    </div>
  );
};

export default JeweleryChartReports;
